import { putCachedConstants, type HBFDatabase } from './db';

interface ManifestLatest {
  tax_year: number;
  version: string;
  sha256: string;
  payload_url: string;
  released_at: string;
  changelog: string;
}

interface Manifest {
  format: string;
  format_version: number;
  latest: ManifestLatest;
}

export type ConstantsUpdateResult =
  | { type: 'no-update' }
  | { type: 'update-available'; version: string; changelog: string }
  | { type: 'rejected'; reason: string }
  | { type: 'fetch-failed'; reason: string };

const MANIFEST_URL = 'https://constants.hudsonbayfinance.ca/manifest.json';
const FETCH_TIMEOUT_MS = 5000;

async function fetchWithTimeout(url: string): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { cache: 'no-store', signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * P2-1: Whether a thrown error is an EXPECTED network / DNS / abort
 * failure (the constants CDN may not be live yet). Expected failures
 * must not log to console.error — only unexpected errors (malformed
 * JSON, type errors, programming bugs) should be surfaced.
 */
function isExpectedNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  // fetch() in browsers throws TypeError for DNS / network / CORS
  // failures; AbortError is thrown by our own timeout.
  if (err.name === 'AbortError') return true;
  if (err instanceof TypeError) return true;
  const msg = err.message.toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network error') ||
    msg.includes('err_name_not_resolved') ||
    msg.includes('enotfound') ||
    msg.includes('econnrefused')
  );
}

export async function checkForConstantsUpdate(
  db: HBFDatabase,
  currentAppliedVersion: string,
  allowedHashes: ReadonlySet<string>
): Promise<ConstantsUpdateResult> {
  try {
    const manifestRes = await fetchWithTimeout(MANIFEST_URL);
    if (!manifestRes.ok) {
      return { type: 'fetch-failed', reason: `Manifest HTTP ${manifestRes.status}` };
    }

    const manifest: Manifest = await manifestRes.json();
    const latest = manifest.latest;

    if (latest.version === currentAppliedVersion) {
      return { type: 'no-update' };
    }

    if (!allowedHashes.has(latest.sha256)) {
      return { type: 'rejected', reason: `SHA-256 ${latest.sha256.slice(0, 12)}… not in allowlist` };
    }

    const payloadRes = await fetchWithTimeout(latest.payload_url);
    if (!payloadRes.ok) {
      return { type: 'fetch-failed', reason: `Payload HTTP ${payloadRes.status}` };
    }

    const payload: unknown = await payloadRes.json();

    await putCachedConstants(db, {
      tax_year: String(latest.tax_year),
      version: latest.version,
      sha256: latest.sha256,
      verified_at: new Date().toISOString(),
      applied_at: null,
      payload,
    });

    return { type: 'update-available', version: latest.version, changelog: latest.changelog };
  } catch (err) {
    // P2-1: Only surface unexpected errors. DNS / network / abort failures
    // are expected while the constants CDN is not yet live and would only
    // add console noise.
    if (!isExpectedNetworkError(err)) {
      console.error('[constants-loader] unexpected error:', err);
    }
    return { type: 'fetch-failed', reason: err instanceof Error ? err.message : String(err) };
  }
}

export async function applyConstantsUpdate(
  db: HBFDatabase,
  taxYear: string,
  version: string
): Promise<void> {
  const record = await db.get('constants_cache', taxYear);
  if (!record || record.version !== version) return;
  await putCachedConstants(db, { ...record, applied_at: new Date().toISOString() });
}
